import type {
  DimensionalEvalResult,
  EvalBadgeVariant,
  EvalResultItem,
  EvalSingletonResponse,
  EvalsResultOpensearchLog,
  PassFailResponse,
  ReferenceOutputDimensionScore,
  ReferenceOutputResponse,
  ScoreResponse,
} from '@/types/eval-v2-types';
import { DIMENSIONAL_EVAL_TYPES } from '@/types/eval-v2-types';

// ---------------------------------------------------------------------------
// Shape detection
//
// `input_results[].response` is opaque on the wire — eval-micro-service emits
// one of three known formats. We sniff structurally rather than relying on a
// discriminator (the backend doesn't include one).
// ---------------------------------------------------------------------------

const REFERENCE_OUTPUT_META_KEYS = new Set([
  'overall_score',
  'overall_explanation',
  'error',
  'missing_elements',
  'added_elements',
]);

function isDimensionalEvalResult(
  r: EvalSingletonResponse,
): r is DimensionalEvalResult {
  // Dimensional evals (goal_achievement, output_quality, factuality, tool_use)
  // have an `eval_type` field with one of the known type values plus a
  // `dimensions` dict. Detect this BEFORE the looser reference-output check.
  const evalType = (r as DimensionalEvalResult)?.eval_type;
  return (
    typeof evalType === 'string' &&
    DIMENSIONAL_EVAL_TYPES.has(evalType as never) &&
    typeof (r as DimensionalEvalResult)?.dimensions === 'object' &&
    (r as DimensionalEvalResult)?.dimensions !== null &&
    !Array.isArray((r as DimensionalEvalResult)?.dimensions)
  );
}

function isReferenceOutputResponse(
  r: EvalSingletonResponse,
): r is ReferenceOutputResponse {
  return (
    typeof (r as ReferenceOutputResponse)?.overall_score === 'number' &&
    typeof (r as ReferenceOutputResponse)?.overall_explanation === 'string'
  );
}

function isPassFailResponse(r: EvalSingletonResponse): r is PassFailResponse {
  const verdict = (r as PassFailResponse)?.verdict;
  return (
    typeof verdict === 'string' &&
    (verdict === 'PASS' || verdict === 'FAIL' || verdict === 'NA')
  );
}

function isScoreResponse(r: EvalSingletonResponse): r is ScoreResponse {
  return (
    typeof (r as ScoreResponse)?.score === 'number' &&
    typeof (r as ScoreResponse)?.explanation === 'string' &&
    !isReferenceOutputResponse(r)
  );
}

function isDimensionScore(v: unknown): v is ReferenceOutputDimensionScore {
  if (typeof v !== 'object' || v === null) return false;
  const cast = v as ReferenceOutputDimensionScore;
  // score may be null when the dimension is N/A (not applicable to the
  // evaluated content) — still a valid DimensionScore shape.
  const scoreOk = cast.score === null || typeof cast.score === 'number';
  return scoreOk && typeof cast.explanation === 'string';
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

function humanize(name: string): string {
  return name.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function scoreBadge(score: number | null): {
  badgeVariant: EvalBadgeVariant;
  badgeText: string;
} {
  // score === null means the dimension was not applicable. Render as
  // an "N/A" badge so reviewers don't read it as a failed 0/10.
  if (score === null) {
    return { badgeVariant: 'score-mid', badgeText: 'N/A' };
  }
  const rounded = Math.round(score);
  const badgeText = `Score: ${score}`;
  if (rounded >= 9) return { badgeVariant: 'pass', badgeText };
  if (rounded >= 8) return { badgeVariant: 'score-high', badgeText };
  return { badgeVariant: 'score-mid', badgeText };
}

// ---------------------------------------------------------------------------
// Per-format card builders
// ---------------------------------------------------------------------------

/**
 * Dynamically extracts test dimensions from a ReferenceOutputResponse.
 * Any key that is not in REFERENCE_OUTPUT_META_KEYS and whose value looks
 * like `{ score: number, explanation: string }` is treated as a test.
 */
function cardFromReferenceOutput(
  evalName: string,
  itemId: string,
  resp: ReferenceOutputResponse,
): EvalResultItem {
  const label = humanize(evalName);
  const children: EvalResultItem[] = [];

  for (const key of Object.keys(resp)) {
    if (REFERENCE_OUTPUT_META_KEYS.has(key)) continue;
    const val = resp[key];
    if (!isDimensionScore(val)) continue;
    children.push({
      id: `${itemId}:${key}`,
      title: humanize(key),
      evalTypeLabel: label,
      ...scoreBadge(val.score),
      detail: val.explanation || undefined,
    });
  }

  return {
    id: `${itemId}:overall`,
    title: label,
    evalTypeLabel: 'Reference Output',
    evalTypeId: 'exemplar',
    ...scoreBadge(resp.overall_score),
    detail: resp.overall_explanation || resp.error || undefined,
    children: children.length > 0 ? children : undefined,
  };
}

function cardFromPassFail(
  evalName: string,
  itemId: string,
  resp: PassFailResponse,
): EvalResultItem {
  let variant: EvalBadgeVariant = 'score-mid';
  let badgeText: string = resp.verdict;
  if (resp.verdict === 'PASS') {
    variant = 'pass';
    badgeText = 'Pass';
  } else if (resp.verdict === 'FAIL') {
    variant = 'fail';
    badgeText = 'Fail';
  } else if (resp.verdict === 'NA') {
    badgeText = 'N/A';
  }
  return {
    id: itemId,
    title: humanize(evalName),
    evalTypeLabel: 'Pass / Fail',
    evalTypeId: 'pass_fail',
    badgeVariant: variant,
    badgeText,
    detail: resp.explanation || undefined,
  };
}

function cardFromScore(
  evalName: string,
  itemId: string,
  resp: ScoreResponse,
): EvalResultItem {
  return {
    id: itemId,
    title: humanize(evalName),
    evalTypeLabel: 'Score',
    evalTypeId: 'score',
    ...scoreBadge(resp.score),
    detail: resp.explanation || undefined,
  };
}

/** Map a dimensional eval response (goal_achievement, output_quality,
 *  factuality, tool_use) into a parent card with one child per dimension. */
function cardFromDimensional(
  evalName: string,
  itemId: string,
  resp: DimensionalEvalResult,
): EvalResultItem {
  const label = humanize(evalName);
  const typeLabel = humanize(resp.eval_type);
  const verdict = (resp.verdict || '').toLowerCase();

  let badgeVariant: EvalBadgeVariant;
  let badgeText: string;
  if (verdict === 'na') {
    badgeVariant = 'score-mid';
    badgeText = 'N/A';
  } else if (verdict === 'error') {
    // Never reached a verdict, so there's no score and it isn't a content
    // failure.
    badgeVariant = 'error';
    badgeText = 'Error';
  } else if (verdict === 'pass') {
    const sb = scoreBadge(resp.overall_score);
    badgeVariant = sb.badgeVariant;
    badgeText = sb.badgeText;
  } else if (verdict === 'fail') {
    badgeVariant = 'fail';
    badgeText = `Score: ${resp.overall_score}`;
  } else {
    const sb = scoreBadge(resp.overall_score);
    badgeVariant = sb.badgeVariant;
    badgeText = sb.badgeText;
  }

  // On an error verdict the backend sends both a summary ("N of M dimensions
  // could not be evaluated") and the underlying cause. `a || b` would show
  // only the summary, so the badge would say something went wrong and never
  // what. Lead with the cause.
  const detailText =
    verdict === 'error' && resp.error
      ? [resp.error, resp.overall_explanation].filter(Boolean).join('\n\n')
      : resp.overall_explanation || resp.error || undefined;

  const children: EvalResultItem[] = [];
  // Render per-dim children even when verdict==='na' (e.g. Quantitative
  // Check on a doc with no numbers): each dimension carries score=null
  // and renders an explicit "N/A" badge, so the breakdown is informative
  // and lets reviewers see exactly what was checked.
  for (const [dimKey, dimVal] of Object.entries(resp.dimensions || {})) {
    if (!isDimensionScore(dimVal)) continue;
    children.push({
      id: `${itemId}:${dimKey}`,
      title: humanize(dimKey),
      evalTypeLabel: typeLabel,
      ...scoreBadge(dimVal.score),
      detail: dimVal.explanation ?? undefined,
    });
  }

  return {
    id: `${itemId}:overall`,
    title: label,
    evalTypeLabel: typeLabel,
    evalTypeId: resp.eval_type,
    badgeVariant,
    badgeText,
    detail: detailText,
    children: children.length > 0 ? children : undefined,
  };
}

function cardFromUnknown(
  evalName: string,
  itemId: string,
  resp: EvalSingletonResponse,
): EvalResultItem {
  return {
    id: itemId,
    title: humanize(evalName),
    evalTypeLabel: 'Eval',
    badgeVariant: 'score-mid',
    badgeText: 'Result',
    detail:
      typeof (resp as { explanation?: unknown }).explanation === 'string'
        ? ((resp as { explanation: string }).explanation as string)
        : undefined,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Flatten OpenSearch eval-result documents into UI cards.
 *
 * For Reference Output evals the mapper produces a single parent card whose
 * `children` array holds one card per dynamically-discovered test dimension
 * (any response key with `{ score, explanation }` that isn't meta).
 *
 * `parentRunRecordIds` (optional) marks which input records were sourced
 * from a parent rerun-from-node session rather than the current run.
 * Items derived from those records get `fromParentRun=true` so the UI
 * can render a "from previous run" badge.
 */
export function mapEvalResultRecordsToTabItems(
  records: EvalsResultOpensearchLog[],
  parentRunRecordIds?: ReadonlySet<string>,
): EvalResultItem[] {
  const items: EvalResultItem[] = [];

  for (const rec of records) {
    const fromParent = parentRunRecordIds?.has(rec.id) ?? false;
    // Recursively stamp `fromParentRun` on the card and every nested
    // child (e.g. per-dimension breakdowns inside a Reference Output
    // eval). Keeping the flag consistent through the tree matters for
    // downstream consumers that walk children programmatically (CSV
    // export, future analytics, etc.); the UI separately decides
    // whether to render the badge per node depth.
    const tagDeep = (item: EvalResultItem): EvalResultItem => ({
      ...item,
      fromParentRun: true,
      ...(item.children ? { children: item.children.map(tagDeep) } : {}),
    });
    const tag = (item: EvalResultItem): EvalResultItem =>
      fromParent ? tagDeep(item) : item;
    for (const evalLog of rec.eval_results ?? []) {
      if (evalLog.error_msg) {
        items.push(
          tag({
            id: `${rec.id}:${evalLog.eval_id}:error`,
            title: humanize(evalLog.eval_name),
            evalTypeLabel: 'Eval',
            badgeVariant: 'error',
            badgeText: 'Error',
            detail: evalLog.error_msg,
          }),
        );
        continue;
      }

      for (const input of evalLog.input_results ?? []) {
        const itemId = `${rec.id}:${evalLog.eval_id}:${input.id}`;
        const resp = input.response;
        if (!resp || typeof resp !== 'object') continue;

        // Order matters: dimensional check is more specific than reference
        // output (both have overall_score + overall_explanation), so it
        // must come first.
        if (isDimensionalEvalResult(resp)) {
          items.push(tag(cardFromDimensional(evalLog.eval_name, itemId, resp)));
        } else if (isReferenceOutputResponse(resp)) {
          items.push(
            tag(cardFromReferenceOutput(evalLog.eval_name, itemId, resp)),
          );
        } else if (isPassFailResponse(resp)) {
          items.push(tag(cardFromPassFail(evalLog.eval_name, itemId, resp)));
        } else if (isScoreResponse(resp)) {
          items.push(tag(cardFromScore(evalLog.eval_name, itemId, resp)));
        } else {
          items.push(tag(cardFromUnknown(evalLog.eval_name, itemId, resp)));
        }
      }
    }
  }

  return items;
}
